import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;
const FLASH_MS = 4500;

const ACTION_COOLDOWN_MS = {
  daily: 86_400_000,
  work: 60 * 60 * 1000,
  meditate: 2 * 60 * 60 * 1000,
  explore: 2 * 60 * 60 * 1000,
};

async function portalPost(path, body, token) {
  const { data } = await axios.post(`${API_BASE}${path}`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(Number(ms) / 1000));
  if (total <= 0) return "ready";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${total}s`;
}

function pickFlash(data) {
  if (data?.message) return String(data.message);
  if (data?.reward != null) {
    return `+${Number(data.reward).toLocaleString()} Essence`;
  }
  if (data?.essenceGain != null || data?.xpGain != null) {
    const bits = [];
    if (data.essenceGain != null) {
      bits.push(`+${Number(data.essenceGain).toLocaleString()} Essence`);
    }
    if (data.xpGain != null) {
      bits.push(`+${Number(data.xpGain).toLocaleString()} XP`);
    }
    return bits.join(", ") || "Done.";
  }
  if (data?.ok) return "Done.";
  return "Done.";
}

function lockTimedAction(portal, actionId) {
  if (!portal || !ACTION_COOLDOWN_MS[actionId]) return portal;
  const remainingMs = ACTION_COOLDOWN_MS[actionId];
  const now = Date.now();
  if (actionId === "daily") {
    return {
      ...portal,
      daily: {
        ...(portal.daily || {}),
        ready: false,
        remainingMs,
        lastAt: new Date(now).toISOString(),
      },
    };
  }
  return {
    ...portal,
    actions: {
      ...(portal.actions || {}),
      [actionId]: {
        ...((portal.actions || {})[actionId] || {}),
        ready: false,
        remainingMs,
        lastAt: new Date(now).toISOString(),
      },
    },
  };
}

function reopenTimedAction(portal, actionId) {
  if (!portal || !ACTION_COOLDOWN_MS[actionId]) return portal;
  if (actionId === "daily") {
    return {
      ...portal,
      daily: {
        ...(portal.daily || {}),
        ready: true,
        remainingMs: 0,
        forceReady: true,
      },
    };
  }
  return {
    ...portal,
    actions: {
      ...(portal.actions || {}),
      [actionId]: {
        ...((portal.actions || {})[actionId] || {}),
        ready: true,
        remainingMs: 0,
        forceReady: true,
      },
    },
  };
}

export default function OverviewActions({ profile, token, onPortalUpdate }) {
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);
  const [petName, setPetName] = useState("");
  const [, setTick] = useState(0);
  const busyRef = useRef(false);
  const profileRef = useRef(profile);
  profileRef.current = profile;

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [flash]);

  // Refresh cooldown labels once a minute while this panel is open.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!token || !profile) return null;

  const run = async (key, fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(key);
    // Lock timed actions immediately so the button can't stay "Ready" while
    // the request is in flight or if the portal payload briefly looks open.
    if (ACTION_COOLDOWN_MS[key] && onPortalUpdate) {
      onPortalUpdate(lockTimedAction(profileRef.current, key));
    }
    try {
      const data = await fn();
      if (data.portal) {
        onPortalUpdate(lockTimedAction(data.portal, key));
      } else if (ACTION_COOLDOWN_MS[key]) {
        onPortalUpdate(lockTimedAction(profileRef.current, key));
      }
      setFlash({ text: pickFlash(data), type: "ok" });
    } catch (err) {
      const msg = err.response?.data?.error || "Something went wrong.";
      // Keep the lock when the server says we're on cooldown; otherwise reopen.
      const cooldownDenied = /remaining|on break|settling|weary/i.test(msg);
      if (!cooldownDenied && ACTION_COOLDOWN_MS[key] && onPortalUpdate) {
        onPortalUpdate(reopenTimedAction(profileRef.current, key));
      }
      setFlash({ text: msg, type: "err" });
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const actions = profile.actions || {};
  const timed = [
    {
      id: "daily",
      label: "Daily",
      path: "/me/daily",
      ready: profile.daily?.ready,
      remainingMs: profile.daily?.remainingMs,
      hint: `Streak ${profile.streak || profile.daily?.streak || 0}`,
    },
    {
      id: "work",
      label: "Work",
      path: "/me/work",
      ready: actions.work?.ready === true,
      remainingMs: actions.work?.remainingMs,
      hint: "1h cooldown · Essence",
    },
    {
      id: "explore",
      label: "Explore",
      path: "/me/explore",
      ready: actions.explore?.ready === true,
      remainingMs: actions.explore?.remainingMs,
      hint: "2h cooldown · Essence + XP",
    },
    {
      id: "meditate",
      label: "Meditate",
      path: "/me/meditate",
      ready: actions.meditate?.ready === true,
      remainingMs: actions.meditate?.remainingMs,
      hint: "2h cooldown · XP",
    },
  ];

  return (
    <div className="portal-stack overview-actions">
      {flash && (
        <div
          className={`portal-toast portal-toast-${flash.type}`}
          role="status"
          aria-live="polite"
        >
          <span>{flash.text}</span>
          <button
            type="button"
            className="portal-toast-dismiss"
            aria-label="Dismiss"
            onClick={() => setFlash(null)}
          >
            ×
          </button>
        </div>
      )}

      <section className="portal-section">
        <h2>Timed actions</h2>
        <p className="portal-muted" style={{ marginBottom: "12px" }}>
          Same cooldowns as Discord - claiming here shares state with the bot.
        </p>
        <div className="overview-action-grid">
          {timed.map((action) => (
            <div key={action.id} className="overview-action-card">
              <strong>{action.label}</strong>
              <span className="portal-muted">{action.hint}</span>
              <span className="overview-action-status">
                {action.ready
                  ? "Ready"
                  : formatRemaining(action.remainingMs)}
              </span>
              <button
                type="button"
                className="profile-btn"
                disabled={busy || !action.ready}
                onClick={() =>
                  run(action.id, () => portalPost(action.path, {}, token))
                }
              >
                {busy === action.id
                  ? "…"
                  : action.ready
                    ? action.label
                    : "On cooldown"}
              </button>
            </div>
          ))}
        </div>

        <div className="overview-quest-block">
          {profile.quest ? (
            <>
              <p className="portal-strong">{profile.quest.name}</p>
              <p className="portal-muted">
                {profile.quest.desc}
                <br />
                {profile.quest.progress}/{profile.quest.goal}
                {profile.quest.completed ? " · Complete" : ""}
              </p>
              <button
                type="button"
                className="profile-btn"
                disabled={
                  busy ||
                  !profile.quest.completed ||
                  profile.quest.rewardClaimed
                }
                onClick={() =>
                  run("quest", () =>
                    portalPost("/me/quest/claim", {}, token),
                  )
                }
              >
                {busy === "quest" ? "…" : "Claim trial"}
              </button>
            </>
          ) : (
            <p className="portal-muted">No active trial right now.</p>
          )}
        </div>
      </section>

      <section className="portal-section">
        <h2>Companions</h2>
        {(profile.pets || []).length === 0 ? (
          <p className="portal-muted">
            No pets yet - check the shop for an egg.
          </p>
        ) : (
          <div className="portal-pet-grid">
            {(profile.pets || []).map((pet) => (
              <div
                key={pet.index}
                className={`portal-pet-card${pet.active ? " active" : ""}`}
              >
                <button
                  type="button"
                  className="portal-pet-select"
                  disabled={busy || pet.active}
                  onClick={() =>
                    run(`pet-${pet.index}`, () =>
                      portalPost(
                        "/me/pet/select",
                        { index: pet.index },
                        token,
                      ),
                    )
                  }
                >
                  <span className="emoji">{pet.emoji}</span>
                  <strong>{pet.name}</strong>
                  <span className="portal-muted">
                    {pet.isHatched
                      ? `Lvl ${pet.level} · ${pet.xp}/${pet.xpGoal}`
                      : `Egg · ${pet.xp}/1000`}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
        {(profile.pets || []).some((p) => p.active) && (
          <div className="portal-pet-actions">
            {(() => {
              const activePet = (profile.pets || []).find((p) => p.active);
              const care = activePet?.care || {};
              const feedLeft = care.feedLeft ?? 2;
              const playLeft = care.playLeft ?? 3;
              const trainReady = care.trainReady !== false;
              const trainMins = Math.ceil(
                (Number(care.trainRemainingMs) || 0) / 60000,
              );
              const trainCost = Math.max(
                1,
                Math.floor(Number(care.trainCost) || 50),
              );
              const petActions = [
                {
                  id: "feed",
                  label: `Feed (${care.feedUsed || 0}/2)`,
                  disabled: !activePet?.isHatched || feedLeft <= 0,
                },
                {
                  id: "play",
                  label: `Play (${care.playUsed || 0}/3)`,
                  disabled: !activePet?.isHatched || playLeft <= 0,
                },
                {
                  id: "train",
                  label: trainReady
                    ? `Train (−${trainCost.toLocaleString()} Essence)`
                    : `Train (${trainMins}m)`,
                  disabled: !activePet?.isHatched || !trainReady,
                },
              ];
              return petActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="profile-btn"
                  disabled={!!busy || action.disabled}
                  title={
                    !activePet?.isHatched
                      ? "Hatch this egg before interacting"
                      : action.id === "train" && !action.disabled
                        ? `Spends ${trainCost.toLocaleString()} Essence`
                        : action.disabled
                          ? "On cooldown / daily limit reached"
                          : undefined
                  }
                  onClick={() => {
                    if (action.id === "train") {
                      const confirmed = window.confirm(
                        `Train ${activePet.name} for ${trainCost.toLocaleString()} Essence?\n\nThis spends Essence and cannot be undone.`,
                      );
                      if (!confirmed) return;
                    }
                    run(action.id, () =>
                      portalPost(
                        "/me/pet/care",
                        { action: action.id },
                        token,
                      ),
                    );
                  }}
                >
                  {busy === action.id ? "…" : action.label}
                </button>
              ));
            })()}
            <input
              className="settings-input"
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              placeholder="Rename companion"
              maxLength={32}
            />
            <button
              type="button"
              className="profile-btn"
              disabled={busy || !petName.trim()}
              onClick={() =>
                run("rename", () =>
                  portalPost(
                    "/me/pet/care",
                    { action: "rename", name: petName.trim() },
                    token,
                  ).then((data) => {
                    setPetName("");
                    return data;
                  }),
                )
              }
            >
              {busy === "rename" ? "…" : "Rename"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
